import React from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Chip,
  Button,
  Collapse,
  Box,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  ShoppingCart,
  AccessTime,
  CheckCircle,
  LocalShipping,
  Cancel,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Order } from "../../../../types";
import OrderDetails from "./OrderDetails";

interface OrderRowProps {
  order: Order;
  isExpanded: boolean;
  onExpandClick: (orderId: string) => void;
  onUpdateStatusClick: (order: Order) => void;
  formatDate: (dateString: string, format: string) => string;
}

const OrderRow: React.FC<OrderRowProps> = ({
  order,
  isExpanded,
  onExpandClick,
  onUpdateStatusClick,
  formatDate,
}) => {
  const { t } = useTranslation();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "warning";
      case "confirmed": return "info";
      case "shipped": return "primary";
      case "delivered": return "success";
      case "cancelled": return "error";
      default: return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending": return <AccessTime fontSize="small" />;
      case "confirmed": return <CheckCircle fontSize="small" />;
      case "shipped": return <LocalShipping fontSize="small" />;
      case "delivered": return <CheckCircle fontSize="small" />;
      case "cancelled": return <Cancel fontSize="small" />;
      default: return null;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "paid": return "#00ff88";
      case "pending": return "#ffaa00";
      case "failed": return "#ff3366";
      default: return "#666";
    }
  };

  return (
    <React.Fragment>
      <TableRow hover>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => onExpandClick(order.id)}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            #{order.id.slice(0, 8)}
          </Typography>
        </TableCell>
        <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
          <Typography variant="body2">
            {formatDate(order.created_at, "MMM dd, yyyy")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(order.created_at, "HH:mm")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {order.shipping_address.full_name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {order.shipping_address.city}
          </Typography>
        </TableCell>
        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
          <Chip
            label={`${order.items?.length || 0} ${t('admin.items').toLowerCase()}`}
            size="small"
            icon={<ShoppingCart fontSize="small" />}
            sx={{
              bgcolor: "rgba(0,212,255,0.1)",
              color: "#00d4ff",
            }}
          />
        </TableCell>
        <TableCell align="right">
          <Typography
            variant="body1"
            fontWeight={700}
            sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
          >
            €{order.total_amount.toFixed(2)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            Com: €{order.commission_amount.toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
          <Chip
            label={order.payment_status}
            size="small"
            sx={{
              bgcolor: `${getPaymentStatusColor(order.payment_status)}20`,
              color: getPaymentStatusColor(order.payment_status),
              border: `1px solid ${getPaymentStatusColor(order.payment_status)}50`,
              fontWeight: 600,
            }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={order.status}
            size="small"
            color={getStatusColor(order.status) as any}
            icon={getStatusIcon(order.status) || undefined}
          />
        </TableCell>
        <TableCell align="center">
          <Button
            size="small"
            variant="outlined"
            onClick={() => onUpdateStatusClick(order)}
            sx={{
              minWidth: { xs: 60, sm: "auto" },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              px: { xs: 1, sm: 2 },
              borderColor: "rgba(0,212,255,0.5)",
              color: "#00d4ff",
              "&:hover": {
                borderColor: "#00d4ff",
                bgcolor: "rgba(0,212,255,0.1)",
              },
            }}
          >
            {t('admin.update')}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0 }}>
          <Collapse
            in={isExpanded}
            timeout="auto"
            unmountOnExit
          >
            <OrderDetails order={order} />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default OrderRow;